// src/components/Footer.jsx
import React from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import MakeenLogo from './MakeenLogo';

const Footer = ({ language, t }) => {
  const isRTL = language === 'ar';
  const [footerRef, footerVisible] = useScrollAnimation(0.1);

  const footerSections = [
    {
      title: t?.footer?.links?.company || 'Company',
      links: [
        { label: t?.footer?.links?.about || 'About', href: '#' },
        { label: t?.footer?.links?.careers || 'Careers', href: '#' },
        { label: t?.footer?.links?.press || 'Press', href: '#' }
      ]
    },
    {
      title: t?.footer?.links?.support || 'Support',
      links: [
        { label: t?.footer?.links?.help || 'Help', href: '#' },
        { label: t?.footer?.links?.contact || 'Contact', href: '#' },
        { label: t?.footer?.links?.faq || 'FAQ', href: '#' }
      ]
    }
  ];

  const contactInfo = [
    { icon: Phone, value: t?.footer?.contact?.phone || '+9647712345678' },
    { icon: Mail, value: t?.footer?.contact?.email || 'info@makeen.com' },
    { icon: MapPin, value: t?.footer?.contact?.address || 'Mansour, Abu Jafaar Al Mansour' }
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com/makeenbank', label: 'Facebook', color: '#1877F2' },
    { icon: Instagram, href: 'https://instagram.com/makeenbank', label: 'Instagram', color: '#E4405F' },
    { icon: Linkedin, href: 'https://linkedin.com/company/makeenbank', label: 'LinkedIn', color: '#0A66C2' },
    { icon: Youtube, href: 'https://youtube.com/@makeenbank', label: 'YouTube', color: '#FF0000' }
  ];

  const legalLinks = [
    { label: t?.footer?.links?.privacy || 'Privacy', href: '#' },
    { label: t?.footer?.links?.terms || 'Terms', href: '#' },
    { label: t?.footer?.links?.security || 'Security', href: '#' }
  ];

  // Updated color scheme
  const bgGradient = 'bg-gradient-to-r from-[#f0f4ff] via-[#e0e8ff] to-[#f0f4ff]'; // light, readable
  const textColor = 'text-gray-800'; // dark enough for contrast
  const subTextColor = 'text-gray-600'; // secondary text
  const borderColor = 'border-gray-300';

  return (
    <footer 
      ref={footerRef}
      id="contact"
      className={`${bgGradient} py-16 relative overflow-hidden ${isRTL ? 'text-right' : 'text-left'}`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`grid md:grid-cols-4 gap-8 mb-8 transition-all duration-1000 ease-out ${
          footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>

          {/* Logo & Description */}
          <div className={`space-y-4 transition-all duration-1200 delay-200 ease-out ${
            footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
            <MakeenLogo language={language} variant="footer" />
            <p className={`${subTextColor} leading-relaxed`} style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '400' }}>
              {t?.footer?.description || 'Leading digital banking solutions for modern businesses and individuals.'}
            </p>

            {/* Social */}
            <div className="mt-6">
              <h5 className={`text-sm font-medium mb-3 ${textColor}`} style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '500' }}>
                {t?.footer?.social?.title || 'Follow Us'}
              </h5>
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110`}
                    style={{ color: social.color }}
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Company & Support Links */}
          {footerSections.map((section, sectionIndex) => (
            <div 
              key={sectionIndex}
              className={`transition-all duration-1200 ease-out ${
                footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${(sectionIndex + 1) * 200 + 200}ms` }}
            >
              <h4 className={`font-semibold mb-4 ${textColor}`} style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600' }}>
                {section.title}
              </h4>
              <div className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <a 
                    key={linkIndex}
                    href={link.href} 
                    className={`${subTextColor} hover:text-gray-800 transition-all duration-300 block`}
                    style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '400' }}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          ))}

          {/* Contact Info */}
          <div className={`transition-all duration-1200 delay-800 ease-out ${
            footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}>
            <h4 className={`font-semibold mb-4 ${textColor}`} style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600' }}>
              {t?.footer?.links?.contact || 'Contact'}
            </h4>
            <div className="space-y-3">
              {contactInfo.map((contact, index) => (
                <div key={index} className="flex items-center">
                  <contact.icon className={`w-4 h-4 mr-3 ${subTextColor}`} />
                  <span className={`${subTextColor}`} style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '400' }}>
                    {contact.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className={`border-t ${borderColor} pt-8`}>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <p className={`${subTextColor} text-sm`} style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '400' }}>
              {t?.footer?.copyright || '© 2024 Makeen Digital Bank. All rights reserved.'}
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              {legalLinks.map((link, index) => (
                <a 
                  key={index}
                  href={link.href} 
                  className={`${subTextColor} hover:text-gray-800 text-sm`}
                  style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '400' }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center pt-6 border-t border-gray-200/50">
            <div className={`${subTextColor} text-xs mb-4 md:mb-0`} style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '400' }}>
              {t?.footer?.tagline || 'Banking Made Simple'}
            </div>
            <div className="flex items-center space-x-4">
              <span className={`${subTextColor} text-xs`} style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '400' }}>
                {t?.footer?.social?.connectWith || 'Connect with us'}
              </span>
              <div className="flex space-x-2">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110`}
                    style={{ color: social.color }}
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <div className="w-24 h-1 bg-gradient-to-r from-[#2beaf4] to-[#582f8f] rounded-full"></div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
